import React, { useState, useEffect, useCallback } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Chips } from 'primereact/chips';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import api, { apiKeysApi } from '../services/api';

// Labels shown in the UI only — the underlying values (retailPrice, clientPrice) are the
// actual API contract field names and must not change, or partner integrations break.
const FIELD_LABELS = {
  retailPrice: 'Retail Price',
  clientPrice: 'Discount Price',
};

const PRICE_FIELD_OPTIONS = [
  { label: FIELD_LABELS.retailPrice, value: 'retailPrice' },
  { label: FIELD_LABELS.clientPrice, value: 'clientPrice' },
];

/** Mirrors apiKeyStore.tierForFields() — for display only, server is the source of truth. */
const tierForFields = (fields = []) => {
  const has = (f) => fields.includes(f);
  if (!has('retailPrice') && !has('clientPrice')) return 'discovery';
  if (has('retailPrice') && has('clientPrice')) return 'retail+client';
  return 'retail';
};

const TIER_LABELS = {
  discovery: 'Discovery (no price)',
  retail: 'Retail Price',
  'retail+client': 'Retail + Discount',
};

// Same palette family as the Users page (role/status pills): green/amber/blue pairs.
const TIER_COLORS = {
  discovery: { bg: '#e3f2fd', color: '#1565c0' },
  retail: { bg: '#d4edda', color: '#155724' },
  'retail+client': { bg: '#fff3cd', color: '#856404' },
};
const ENV_COLORS = {
  live: { bg: '#d4edda', color: '#155724' },
  test: { bg: '#fff3cd', color: '#856404' },
};
const STATUS_COLORS = {
  active: { bg: '#d4edda', color: '#155724' },
  revoked: { bg: '#f8d7da', color: '#721c24' },
};

/** Rounded pill badge, same shape/padding as the Users page role/status badges. */
const pillStyle = (bg, color) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '0.375rem 0.875rem',
  backgroundColor: bg,
  color,
  borderRadius: '16px',
  fontSize: '0.8125rem',
  fontWeight: '600',
  letterSpacing: '0.025em',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  whiteSpace: 'nowrap',
});

const ENV_OPTIONS = [
  { label: 'Live', value: 'live' },
  { label: 'Test', value: 'test' },
];

const emptyForm = {
  clientName: '',
  env: 'live',
  allowedFields: ['retailPrice'],
  allowedStates: [],
  allowedProviders: [],
  rateLimitPerMin: null,
};

const fieldLabelStyle = {
  marginBottom: '0.75rem',
  fontWeight: '600',
  color: '#495057',
  fontSize: '0.95rem',
};

const helpTextStyle = {
  display: 'block',
  marginTop: '0.5rem',
  color: '#6c757d',
  fontSize: '0.85rem',
};

const ApiKeys = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  const [dialogVisible, setDialogVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  // The one-time reveal of a freshly created key.
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);

  const isMasterAdmin = currentUser?.role === 'masteradmin';

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiKeysApi.list();
      setClients(data.clients || []);
    } catch (err) {
      setError(err.message || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data);
    } catch (err) {
      console.error('Failed to fetch current user:', err);
    }
  }, []);

  useEffect(() => {
    fetchClients();
    fetchCurrentUser();
  }, [fetchClients, fetchCurrentUser]);

  const handleAdd = () => {
    setFormData(emptyForm);
    setError('');
    setDialogVisible(true);
  };

  const handleSubmit = async () => {
    if (!formData.clientName.trim()) {
      setError('Client name is required');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const result = await apiKeysApi.create({
        clientName: formData.clientName.trim(),
        env: formData.env,
        allowedFields: formData.allowedFields,
        allowedStates: (formData.allowedStates || []).map((s) => s.toUpperCase()),
        allowedProviders: (formData.allowedProviders || []).map((p) => p.toLowerCase()),
        rateLimitPerMin: formData.rateLimitPerMin,
      });
      setDialogVisible(false);
      setNewKey({ rawKey: result.rawKey, client: result.client });
      setCopied(false);
      fetchClients();
    } catch (err) {
      setError(err.message || 'Failed to create API key');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = (client) => {
    confirmDialog({
      message: `Revoke the API key for "${client.clientName}" (${client.keyPrefix})? Any integration using it will immediately stop working. This cannot be undone.`,
      header: 'Revoke API Key',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await apiKeysApi.revoke(client.keyId);
          setSuccess('API key revoked successfully');
          fetchClients();
          setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
          setError(err.message || 'Failed to revoke API key');
        }
      },
    });
  };

  const copyKey = async () => {
    if (!newKey) return;
    try {
      await navigator.clipboard.writeText(newKey.rawKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (_) {
      // Clipboard may be blocked; the value is selectable in the field regardless.
    }
  };

  const formatDate = (value) => (value ? new Date(value).toLocaleString() : '—');

  const envBodyTemplate = (row) => {
    const c = ENV_COLORS[row.env] || ENV_COLORS.test;
    return <span style={pillStyle(c.bg, c.color)}>{row.env}</span>;
  };

  const statusBodyTemplate = (row) => {
    const c = row.active ? STATUS_COLORS.active : STATUS_COLORS.revoked;
    return <span style={pillStyle(c.bg, c.color)}>{row.active ? 'Active' : 'Revoked'}</span>;
  };

  const tierBodyTemplate = (row) => {
    const tier = row.tier || tierForFields(row.allowedFields || []);
    const c = TIER_COLORS[tier] || TIER_COLORS.discovery;
    return <span style={pillStyle(c.bg, c.color)}>{TIER_LABELS[tier] || tier}</span>;
  };

  const fieldsBodyTemplate = (row) =>
    (row.allowedFields || []).map((f) => FIELD_LABELS[f] || f).join(', ') || '—';
  const statesBodyTemplate = (row) =>
    row.allowedStates && row.allowedStates.length ? row.allowedStates.join(', ') : 'ALL';
  const providersBodyTemplate = (row) =>
    row.allowedProviders && row.allowedProviders.length ? row.allowedProviders.join(', ') : 'ALL';
  const rateLimitBodyTemplate = (row) => (row.rateLimitPerMin ? `${row.rateLimitPerMin}/min` : 'default');
  const lastUsedBodyTemplate = (row) => formatDate(row.lastUsedAt);

  const actionsBodyTemplate = (row) => (
    <Button
      icon="pi pi-ban"
      className="p-button-rounded p-button-text p-button-danger p-button-sm"
      disabled={!row.active || !isMasterAdmin}
      tooltip={!isMasterAdmin ? 'Masteradmin only' : 'Revoke'}
      tooltipOptions={{ position: 'top' }}
      onClick={() => handleRevoke(row)}
    />
  );

  const selectedTier = tierForFields(formData.allowedFields);

  return (
    <div
      style={{
        padding: '2rem 2.5rem',
        maxWidth: '1400px',
        margin: '0 auto',
        minHeight: 'calc(100vh - 100px)',
      }}
    >
      <div
        style={{
          marginBottom: '2.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <h1
            style={{
              marginBottom: '0.5rem',
              fontSize: '2rem',
              fontWeight: '600',
              color: '#2c3e50',
            }}
          >
            API Keys
          </h1>
          <p style={{ marginBottom: 0, color: '#6c757d', fontSize: '0.95rem' }}>
            Third-party access to the read-only fuel-prices feed.
          </p>
        </div>
        <Button
          label="Issue API Key"
          icon="pi pi-plus"
          iconPos="left"
          onClick={handleAdd}
          className="p-button-lg"
          style={{ padding: '0.75rem 2rem', fontSize: '1rem', fontWeight: '500' }}
          disabled={!isMasterAdmin}
          tooltip={!isMasterAdmin ? 'Only masteradmin can issue keys' : undefined}
          tooltipOptions={{ position: 'left' }}
        />
      </div>

      {error && (
        <Message severity="error" text={error} style={{ marginBottom: '1.5rem' }} onClose={() => setError('')} />
      )}

      {success && (
        <Message
          severity="success"
          text={success}
          style={{ marginBottom: '1.5rem' }}
          onClose={() => setSuccess('')}
        />
      )}

      <Card style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)', padding: '1.5rem' }}>
        <DataTable
          value={clients}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 20, 50]}
          emptyMessage="No API keys issued yet"
          style={{ width: '100%' }}
          className="p-datatable-sm"
        >
          <Column field="clientName" header="Client" sortable style={{ minWidth: '160px' }} />
          <Column field="keyPrefix" header="Key ID" style={{ minWidth: '180px' }} />
          <Column field="env" header="Env" body={envBodyTemplate} sortable style={{ minWidth: '110px' }} />
          <Column header="Tier" body={tierBodyTemplate} style={{ minWidth: '170px' }} />
          <Column header="Fields" body={fieldsBodyTemplate} style={{ minWidth: '190px' }} />
          <Column header="States" body={statesBodyTemplate} style={{ minWidth: '110px' }} />
          <Column header="Providers" body={providersBodyTemplate} style={{ minWidth: '130px' }} />
          <Column header="Rate limit" body={rateLimitBodyTemplate} style={{ minWidth: '120px' }} />
          <Column field="requestCount" header="Requests" sortable style={{ minWidth: '110px' }} />
          <Column header="Last used" body={lastUsedBodyTemplate} sortable style={{ minWidth: '180px' }} />
          <Column field="active" header="Status" body={statusBodyTemplate} sortable style={{ minWidth: '120px' }} />
          <Column
            header="Actions"
            body={actionsBodyTemplate}
            style={{ minWidth: '100px', textAlign: 'center' }}
            exportable={false}
          />
        </DataTable>
      </Card>

      <Dialog
        header={
          <div style={{ display: 'flex', alignItems: 'center', padding: '0.5rem 0.5rem' }}>
            <i className="pi pi-key" style={{ marginRight: '0.75rem', fontSize: '1.25rem' }}></i>
            <span style={{ fontSize: '1.25rem', fontWeight: '600' }}>Issue a new API key</span>
          </div>
        }
        visible={dialogVisible}
        style={{ width: '90%', maxWidth: '560px' }}
        contentStyle={{ padding: '2rem 2.5rem' }}
        onHide={() => setDialogVisible(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', padding: '1.5rem 2.5rem' }}>
            <Button
              label="Cancel"
              icon="pi pi-times"
              iconPos="left"
              onClick={() => setDialogVisible(false)}
              className="p-button-text p-button-lg"
              style={{ padding: '0.75rem 1.5rem', fontSize: '1rem' }}
              disabled={submitting}
            />
            <Button
              label="Create Key"
              icon={submitting ? null : 'pi pi-check'}
              iconPos="left"
              onClick={handleSubmit}
              disabled={submitting}
              loading={submitting}
              className="p-button-lg"
              style={{ padding: '0.75rem 1.5rem', fontSize: '1rem', minWidth: '140px' }}
            />
          </div>
        }
      >
        <div className="p-field" style={{ marginBottom: '1.75rem' }}>
          <label htmlFor="clientName" className="p-d-block" style={fieldLabelStyle}>
            Client name *
          </label>
          <InputText
            id="clientName"
            value={formData.clientName}
            onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
            style={{ width: '100%', padding: '0.75rem', fontSize: '1rem' }}
            placeholder="e.g. Acme Logistics"
          />
        </div>

        <div className="p-field" style={{ marginBottom: '1.75rem' }}>
          <label htmlFor="env" className="p-d-block" style={fieldLabelStyle}>
            Environment
          </label>
          <Dropdown
            id="env"
            value={formData.env}
            options={ENV_OPTIONS}
            onChange={(e) => setFormData({ ...formData, env: e.value })}
            style={{ width: '100%' }}
          />
        </div>

        <div className="p-field" style={{ marginBottom: '1.75rem' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '0.75rem',
            }}
          >
            <label htmlFor="allowedFields" className="p-d-block" style={{ ...fieldLabelStyle, marginBottom: 0 }}>
              Price fields
            </label>
            <span style={pillStyle(TIER_COLORS[selectedTier].bg, TIER_COLORS[selectedTier].color)}>
              {TIER_LABELS[selectedTier]}
            </span>
          </div>
          <MultiSelect
            id="allowedFields"
            value={formData.allowedFields}
            options={PRICE_FIELD_OPTIONS}
            onChange={(e) => setFormData({ ...formData, allowedFields: e.value })}
            placeholder="Leave empty for Discovery (no price)"
            display="chip"
            style={{ width: '100%', minHeight: '48px' }}
          />
          <small style={helpTextStyle}>
            Base cost and supplier are never exposed. Grant Discount Price only to entitled partners — they must
            also send a per-user reference to unlock it for a specific end user (see CONSUMER_GUIDE.md).
          </small>
        </div>

        {selectedTier === 'discovery' && (
          <div
            style={{
              background: '#e7f3ff',
              border: '1px solid #b3d9ff',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.75rem',
            }}
          >
            <p style={{ margin: 0, color: '#0056b3', fontSize: '0.9rem' }}>
              <i className="pi pi-info-circle" style={{ marginRight: '0.5rem' }}></i>
              Discovery tier: station name, address and location only, no price data at all — for partners
              evaluating coverage before a paid data agreement.
            </p>
          </div>
        )}

        <div className="p-field" style={{ marginBottom: '1.75rem' }}>
          <label htmlFor="allowedStates" className="p-d-block" style={fieldLabelStyle}>
            Allowed states (optional)
          </label>
          <Chips
            id="allowedStates"
            className="ns-chips-full"
            value={formData.allowedStates}
            onChange={(e) => setFormData({ ...formData, allowedStates: e.value })}
            placeholder="e.g. IA, NE (blank = all)"
            style={{ width: '100%' }}
          />
          <small style={helpTextStyle}>Press Enter after each 2-letter state code.</small>
        </div>

        <div className="p-field" style={{ marginBottom: '1.75rem' }}>
          <label htmlFor="allowedProviders" className="p-d-block" style={fieldLabelStyle}>
            Allowed providers (optional)
          </label>
          <Chips
            id="allowedProviders"
            className="ns-chips-full"
            value={formData.allowedProviders}
            onChange={(e) => setFormData({ ...formData, allowedProviders: e.value })}
            placeholder="e.g. caseys, tapetro (blank = all)"
            style={{ width: '100%' }}
          />
        </div>

        {/*
          PrimeReact's Chips renders its live <input> without an explicit width, so it
          falls back to the browser default (~20 characters) and clips longer
          placeholders instead of the container's own width. Force it to fill.
        */}
        <style>{`
          .ns-chips-full .p-chips-multiple-container {
            width: 100%;
            min-height: 48px;
          }
          .ns-chips-full .p-chips-multiple-container .p-chips-input-token,
          .ns-chips-full .p-chips-multiple-container .p-chips-input-token input {
            width: 100% !important;
          }
        `}</style>

        <div className="p-field" style={{ marginBottom: '0.5rem' }}>
          <label htmlFor="rateLimitPerMin" className="p-d-block" style={fieldLabelStyle}>
            Rate limit (requests/min, optional)
          </label>
          <InputNumber
            id="rateLimitPerMin"
            value={formData.rateLimitPerMin}
            onValueChange={(e) => setFormData({ ...formData, rateLimitPerMin: e.value })}
            placeholder="Leave blank for default"
            min={1}
            useGrouping={false}
            style={{ width: '100%' }}
            inputStyle={{ width: '100%', padding: '0.75rem', fontSize: '1rem' }}
          />
        </div>

        {error && <Message severity="error" text={error} style={{ marginTop: '1rem', width: '100%' }} />}
      </Dialog>

      <Dialog
        header={
          <div style={{ display: 'flex', alignItems: 'center', padding: '0.5rem 0.5rem' }}>
            <i className="pi pi-check-circle" style={{ marginRight: '0.75rem', fontSize: '1.25rem', color: '#155724' }}></i>
            <span style={{ fontSize: '1.25rem', fontWeight: '600' }}>API key created</span>
          </div>
        }
        visible={!!newKey}
        style={{ width: '90%', maxWidth: '600px' }}
        contentStyle={{ padding: '2rem 2.5rem' }}
        onHide={() => setNewKey(null)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '1.5rem 2.5rem' }}>
            <Button
              label="Done"
              icon="pi pi-check"
              onClick={() => setNewKey(null)}
              className="p-button-lg"
              style={{ padding: '0.75rem 1.5rem', fontSize: '1rem', minWidth: '120px' }}
            />
          </div>
        }
      >
        {newKey && (
          <>
            <Message
              severity="warn"
              text="Copy this key now — it is shown only once and cannot be recovered."
              style={{ marginBottom: '1.5rem', width: '100%' }}
            />
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'stretch', marginBottom: '1.75rem' }}>
              <InputText
                value={newKey.rawKey}
                readOnly
                onFocus={(e) => e.target.select()}
                style={{ flexGrow: 1, padding: '0.75rem', fontSize: '0.95rem', fontFamily: 'monospace' }}
              />
              <Button
                icon={copied ? 'pi pi-check' : 'pi pi-copy'}
                label={copied ? 'Copied' : 'Copy'}
                onClick={copyKey}
                style={{ padding: '0.75rem 1.25rem' }}
              />
            </div>

            <div
              style={{
                background: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                padding: '1.25rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.9rem' }}>
                <span style={{ fontWeight: '500', color: '#495057', fontSize: '0.9rem' }}>Client</span>
                <span style={{ color: '#2c3e50', fontSize: '0.9rem', fontWeight: '600' }}>{newKey.client.clientName}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.9rem' }}>
                <span style={{ fontWeight: '500', color: '#495057', fontSize: '0.9rem' }}>Tier</span>
                <span
                  style={pillStyle(
                    TIER_COLORS[newKey.client.tier]?.bg || TIER_COLORS.discovery.bg,
                    TIER_COLORS[newKey.client.tier]?.color || TIER_COLORS.discovery.color
                  )}
                >
                  {TIER_LABELS[newKey.client.tier] || newKey.client.tier}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.9rem' }}>
                <span style={{ fontWeight: '500', color: '#495057', fontSize: '0.9rem' }}>Fields</span>
                <span style={{ color: '#2c3e50', fontSize: '0.9rem' }}>
                  {(newKey.client.allowedFields || []).map((f) => FIELD_LABELS[f] || f).join(', ') ||
                    'none (Discovery)'}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: '500', color: '#495057', fontSize: '0.9rem' }}>States</span>
                <span style={{ color: '#2c3e50', fontSize: '0.9rem' }}>
                  {(newKey.client.allowedStates || []).join(', ') || 'ALL'}
                </span>
              </div>
            </div>
          </>
        )}
      </Dialog>

      <ConfirmDialog />
    </div>
  );
};

export default ApiKeys;
